import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

/**
 * Generate an annotated diff PDF from file2's bytes + the change list.
 *
 * Returns a Uint8Array. Caller saves via useFileDialog.
 *
 * @param {object} args
 * @param {ArrayBuffer|Uint8Array} args.file2Bytes — the "new" version PDF
 * @param {Array} [args.changes] — change list from the compare session
 * @param {boolean} [args.includeSummary] — prepend a summary page
 * @param {string} [args.file1Name] — for the summary page text
 * @param {string} [args.file2Name] — for the summary page text
 */
export async function exportDiffPdf({
  file2Bytes,
  changes = [],
  includeSummary = true,
  file1Name = 'file1.pdf',
  file2Name = 'file2.pdf',
}) {
  const doc = await PDFDocument.load(file2Bytes);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  // Annotate change locations on each page. We use simple colored rectangles
  // — pdf-lib's higher-level annotation API is overkill for our needs.
  for (const change of changes) {
    if (!change.bbox || change.page == null) continue;
    if (change.page >= doc.getPageCount()) continue;
    const page = doc.getPage(change.page);
    const { x, y, w, h } = change.bbox;
    const color =
      change.type === 'added'    ? rgb(0.06, 0.73, 0.51) :  // emerald
      change.type === 'removed'  ? rgb(0.94, 0.27, 0.27) :  // red
      change.type === 'modified' ? rgb(0.96, 0.62, 0.04) :  // amber
                                   rgb(0.94, 0.27, 0.27);   // visual → red
    page.drawRectangle({
      x, y,
      width: w, height: h,
      borderColor: color,
      borderWidth: 1.5,
      color,
      opacity: 0.2,
      borderOpacity: 0.7,
    });
  }

  if (includeSummary) {
    const summary = doc.insertPage(0, [612, 792]);
    const indigo = rgb(0.31, 0.27, 0.90);
    summary.drawText('PDF Comparison Report', {
      x: 50, y: 720, size: 24, font: fontBold, color: indigo,
    });
    summary.drawText(`Original: ${file1Name}`, {
      x: 50, y: 680, size: 11, font, color: rgb(0.3, 0.3, 0.3),
    });
    summary.drawText(`New version: ${file2Name}`, {
      x: 50, y: 660, size: 11, font, color: rgb(0.3, 0.3, 0.3),
    });
    summary.drawText(`Changes detected: ${changes.length}`, {
      x: 50, y: 640, size: 11, font, color: rgb(0.3, 0.3, 0.3),
    });
    summary.drawText(`Generated: ${new Date().toISOString().slice(0, 10)} by Scrivar`, {
      x: 50, y: 620, size: 11, font, color: rgb(0.3, 0.3, 0.3),
    });
    summary.drawText('Color key:', {
      x: 50, y: 570, size: 12, font: fontBold, color: rgb(0.1, 0.1, 0.1),
    });
    const items = [
      { label: 'Added content', color: rgb(0.06, 0.73, 0.51), y: 545 },
      { label: 'Removed content', color: rgb(0.94, 0.27, 0.27), y: 525 },
      { label: 'Modified content', color: rgb(0.96, 0.62, 0.04), y: 505 },
      { label: 'Visual change', color: rgb(0.94, 0.27, 0.27), y: 485 },
    ];
    for (const it of items) {
      summary.drawRectangle({
        x: 60, y: it.y, width: 12, height: 12, color: it.color, opacity: 0.5, borderColor: it.color, borderWidth: 1,
      });
      summary.drawText(it.label, {
        x: 80, y: it.y + 2, size: 11, font, color: rgb(0.3, 0.3, 0.3),
      });
    }
  }

  return await doc.save();
}
